import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { FiSave } from 'react-icons/fi'
import type { SupportReceive } from '@/types'
import { formatDateTime } from '@/utils/formatUtils'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { DropdownSelect } from '@/components/common/DropdownSelect'

interface SupportReceiveModalProps {
  t: TFunction
  isOpen: boolean
  onClose: () => void
  selectedReceive: SupportReceive | null
  editStatus: string
  editNotes: string
  payloadPreview: string
  supportStatuses: string[]
  onStatusChange: (value: string) => void
  onNotesChange: (value: string) => void
  onSave: () => void
  onRefresh?: () => Promise<unknown> | void
  isSaving: boolean
  isRefreshing?: boolean
}

export function SupportReceiveModal({
  t,
  isOpen,
  onClose,
  selectedReceive,
  editStatus,
  editNotes,
  payloadPreview,
  supportStatuses,
  onStatusChange,
  onNotesChange,
  onSave,
  onRefresh,
  isSaving,
  isRefreshing = false,
}: SupportReceiveModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pr={12}>
          <HStack justify="space-between" spacing={3}>
            <Text>{t('support.receives.modal.title')}</Text>
            {onRefresh && (
              <RefreshIconButton
                label={t('common.refresh')}
                tooltipPlacement="left"
                onClick={() => void onRefresh()}
                isLoading={isRefreshing}
                isDisabled={isRefreshing}
                flexShrink={0}
              />
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {selectedReceive && (
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t('support.receives.table.ticketId')}
                  </Text>
                  <Text fontSize="sm" fontFamily="mono">
                    {selectedReceive.ticketId || '—'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t('support.receives.table.eventType')}
                  </Text>
                  <Text fontSize="sm" fontFamily="mono">
                    {selectedReceive.eventType || '—'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t('support.receives.table.receivedAt')}
                  </Text>
                  <Text fontSize="sm">
                    {formatDateTime(selectedReceive.receivedAt as string)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t('support.receives.table.source')}
                  </Text>
                  <Text fontSize="sm">{selectedReceive.source || '—'}</Text>
                </Box>
              </SimpleGrid>

              <FormControl>
                <FormLabel>{t('support.receives.modal.status')}</FormLabel>
                <DropdownSelect
                  value={editStatus}
                  options={supportStatuses.map(status => ({
                    value: status,
                    label: status.replace(/_/g, ' '),
                  }))}
                  onChange={onStatusChange}
                  ariaLabel={t('support.receives.modal.status')}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('support.receives.modal.notes')}</FormLabel>
                <Textarea
                  value={editNotes}
                  onChange={(event) => onNotesChange(event.target.value)}
                  placeholder={t('support.receives.modal.notesPlaceholder')}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('support.receives.modal.payload')}</FormLabel>
                <Textarea
                  value={payloadPreview}
                  readOnly
                  minH="200px"
                  fontFamily="mono"
                  fontSize="xs"
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={onSave}
              isLoading={isSaving}
              leftIcon={<FiSave />}
            >
              {t('common.save')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
